import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, Eye, Loader2, PlusSquare, Save, ShieldCheck, X } from 'lucide-react'
import axios from '../../../../config/axios'
import AttachmentApiService from '../../../../services/AttachmentApiService'
import UserContext from '../../../../contexts/UserContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import {
  BackButton,
  Body,
  Card,
  FieldGroup,
  FieldsGrid,
  HeaderActions,
  HeaderApprovalName,
  HeaderProductName,
  HeaderRef,
  HeaderSubtitle,
  HeaderTitle,
  HeaderTitleRow,
  Input,
  Label,
  ModalHeader,
  ModalShell,
  OutlineButton,
  Overlay,
  PrimaryButton,
  SectionTitle,
  SimpleModalBody,
  SimpleModalBox,
  SimpleModalCloseBtn,
  SimpleModalFooter,
  SimpleModalHeader,
  SimpleModalOverlay,
  SimpleModalTitle,
  StatusBadge,
} from './styles'
import { ApprovalApi } from './api'
import {
  ChecklistItem,
  GeneralFile,
  ProductApprovalDetail,
  ProductApprovalStatus,
} from './types'
import { GalleryFile } from '../../../../components/AttachmentGalleryModal'
import ImagesSection from './ImagesSection'
import ChecklistSection from './ChecklistSection'

interface Props {
  approvalId: number
  onClose: () => void
  onChanged?: () => void
}

const statusLabel = (status: ProductApprovalStatus) =>
  status === 'IN_PROGRESS'
    ? 'Em andamento'
    : status === 'APPROVED'
      ? 'Aprovado'
      : 'Reprovado'

const toDateInputValue = (iso: string | null) => {
  if (!iso) return ''
  return iso.slice(0, 10)
}

const isCompleteDate = (date: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false
  const [year, month, day] = date.split('-').map(Number)
  if (year < 1900 || year > 2100) return false
  const parsed = new Date(`${date}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return false
  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() + 1 === month &&
    parsed.getDate() === day
  )
}

const toIso = (date: string) => {
  if (!date || !isCompleteDate(date)) return null
  return new Date(`${date}T00:00:00`).toISOString()
}

const ApprovationModal: React.FC<Props> = ({ approvalId, onClose, onChanged }) => {
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const { setFeedback } = useContext(FeedbackContext)
  const isAdmin = user?.role === 'ADMIN_GLOBAL' || user?.role === 'TENANT_ADMIN'
  const currentUserId = user?.sub

  const [approval, setApproval] = useState<ProductApprovalDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [startDate, setStartDate] = useState('')
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('')
  const [sizesInput, setSizesInput] = useState('')
  const [showExitConfirm, setShowExitConfirm] = useState(false)

  const [showSendToPipelineModal, setShowSendToPipelineModal] = useState(false)
  const [availablePipelines, setAvailablePipelines] = useState<any[]>([])
  const [selectedTargetPipelineId, setSelectedTargetPipelineId] = useState<number | null>(null)
  const [availableTargetSteps, setAvailableTargetSteps] = useState<any[]>([])
  const [selectedTargetStepId, setSelectedTargetStepId] = useState<number | null>(null)
  const [sendingToPipeline, setSendingToPipeline] = useState(false)
  const [loadingPipelines, setLoadingPipelines] = useState(false)
  const [loadingSteps, setLoadingSteps] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const data = await ApprovalApi.getById(approvalId)
      setApproval(data)
      setName(data.name)
      setStartDate(toDateInputValue(data.startDate))
      setExpectedDeliveryDate(toDateInputValue(data.expectedDeliveryDate))
      setSizesInput((data.sizes ?? []).join(', '))
    } catch (err) {
      setFeedback({
        isOpen: true,
        message: 'Erro ao carregar aprovação.',
        statusOk: false,
      })
    } finally {
      setLoading(false)
    }
  }, [approvalId, setFeedback])

  useEffect(() => {
    reload()
  }, [approvalId, reload])

  const locked = !!approval?.lockedAt

  const isDirty = useMemo(() => {
    if (!approval) return false
    const originalName = approval.name ?? ''
    const originalStartDate = toDateInputValue(approval.startDate)
    const originalExpectedDate = toDateInputValue(approval.expectedDeliveryDate)
    const originalSizes = (approval.sizes ?? []).join(', ')
    return (
      name !== originalName ||
      startDate !== originalStartDate ||
      expectedDeliveryDate !== originalExpectedDate ||
      sizesInput !== originalSizes
    )
  }, [approval, name, startDate, expectedDeliveryDate, sizesInput])

  const handleSave = async () => {
    if (!approval || locked || saving) return
    setSaving(true)
    try {
      const parsedSizes = sizesInput
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      const payload: Parameters<typeof ApprovalApi.update>[1] = {
        name: name.trim() || approval.name,
        startDate: toIso(startDate),
        expectedDeliveryDate: toIso(expectedDeliveryDate),
        sizes: parsedSizes,
      }
      await ApprovalApi.update(approval.id, payload)
      setApproval((prev) =>
        prev
          ? {
              ...prev,
              name: payload.name ?? prev.name,
              startDate: payload.startDate ?? null,
              expectedDeliveryDate: payload.expectedDeliveryDate ?? null,
              sizes: payload.sizes ?? prev.sizes,
            }
          : prev,
      )
      onChanged?.()
      setFeedback({
        isOpen: true,
        message: 'Alterações salvas com sucesso.',
        statusOk: true,
      })
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao salvar alterações.',
        statusOk: false,
      })
      throw err
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (!approval) return
    setName(approval.name)
    setStartDate(toDateInputValue(approval.startDate))
    setExpectedDeliveryDate(toDateInputValue(approval.expectedDeliveryDate))
    setSizesInput((approval.sizes ?? []).join(', '))
  }

  const handleAttemptClose = () => {
    if (isDirty) {
      setShowExitConfirm(true)
    } else {
      onClose()
    }
  }

  const handleConfirmSaveAndClose = async () => {
    if (saving) return
    try {
      await handleSave()
      setShowExitConfirm(false)
      onClose()
    } catch (error) {
      console.error(error)
    }
  }

  const handleDiscardAndClose = () => {
    handleCancel()
    setShowExitConfirm(false)
    onClose()
  }

  const handleViewCard = () => {
    if (!approval?.ticketId) return
    onClose()
    const queryParams = new URLSearchParams({
      ticketId: String(approval.ticketId),
      returnTo: '/panel/collection/funnel',
    })
    if (approval.ticketPipelineId) {
      queryParams.set('pipelineId', String(approval.ticketPipelineId))
    }
    navigate(`/panel/pipeline/ticket?${queryParams.toString()}`)
  }

  const handleUploadImage = async (slot: 'front' | 'side' | 'back', file: File) => {
    if (!approval || locked) return
    try {
      const uploaded = await ApprovalApi.uploadFile(file)
      const fieldKey =
        slot === 'front' ? 'frontImageId' : slot === 'side' ? 'sideImageId' : 'backImageId'
      const previewKey = slot === 'front' ? 'FrontImage' : slot === 'side' ? 'SideImage' : 'BackImage'
      await ApprovalApi.update(approval.id, { [fieldKey]: uploaded.id })
      setApproval({
        ...approval,
        [fieldKey]: uploaded.id,
        [previewKey]: uploaded,
      } as ProductApprovalDetail)
      onChanged?.()
      ApprovalApi.getById(approval.id)
        .then((fresh) => {
          setApproval(fresh)
        })
        .catch(() => {})
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao enviar imagem.',
        statusOk: false,
      })
    }
  }

  const handleSelectGalleryImage = async (slot: 'front' | 'side' | 'back', file: GalleryFile) => {
    if (!approval || locked) return
    try {
      const fieldKey =
        slot === 'front' ? 'frontImageId' : slot === 'side' ? 'sideImageId' : 'backImageId'
      const previewKey = slot === 'front' ? 'FrontImage' : slot === 'side' ? 'SideImage' : 'BackImage'
      await ApprovalApi.update(approval.id, { [fieldKey]: file.id })
      let signedUrl = file.signedUrl || file.fileUrl
      if (
        !signedUrl.includes('X-Goog-Signature') &&
        !signedUrl.includes('X-Amz-Signature') &&
        !signedUrl.startsWith('blob:') &&
        !signedUrl.startsWith('data:')
      ) {
        try {
          const freshSigned = await AttachmentApiService.getSignedUrl(file.fileUrl)
          if (freshSigned) signedUrl = freshSigned
        } catch (error) {
          console.error(error)
        }
      }
      const generalFile: GeneralFile = {
        id: file.id,
        fileUrl: signedUrl,
        signedUrl,
        fileName: file.fileName,
        originalName: file.originalName || file.title,
        mimeType: file.mimeType,
      }
      setApproval({
        ...approval,
        [fieldKey]: file.id,
        [previewKey]: generalFile,
      } as ProductApprovalDetail)
      onChanged?.()
      ApprovalApi.getById(approval.id)
        .then((fresh) => {
          setApproval(fresh)
        })
        .catch(() => {})
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao vincular imagem da galeria.',
        statusOk: false,
      })
    }
  }

  const handleRemoveImage = async (slot: 'front' | 'side' | 'back') => {
    if (!approval || locked) return
    try {
      const fieldKey =
        slot === 'front' ? 'frontImageId' : slot === 'side' ? 'sideImageId' : 'backImageId'
      const previewKey = slot === 'front' ? 'FrontImage' : slot === 'side' ? 'SideImage' : 'BackImage'
      await ApprovalApi.update(approval.id, { [fieldKey]: null })
      setApproval({
        ...approval,
        [fieldKey]: null,
        [previewKey]: null,
      } as ProductApprovalDetail)
      onChanged?.()
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao remover imagem.',
        statusOk: false,
      })
    }
  }

  const handleItemsChanged = (items: ChecklistItem[]) => {
    if (!approval) return
    setApproval({ ...approval, ChecklistItems: items })
    onChanged?.()
    ApprovalApi.getById(approval.id).then((fresh) => {
      setApproval(fresh)
    })
  }

  const [showPromoteModal, setShowPromoteModal] = useState(false)
  const [promoteCode, setPromoteCode] = useState('')
  const [promoteName, setPromoteName] = useState('')
  const [promotingBusy, setPromotingBusy] = useState(false)

  const handlePromote = () => {
    if (!approval) return
    const parentCode = approval.Product?.productCode || ''
    const base = parentCode ? (parentCode.endsWith('.') ? parentCode : `${parentCode}.`) : ''
    setPromoteCode(base)
    setPromoteName('Mostruário')
    setShowPromoteModal(true)
  }

  const handleConfirmPromoteToShowroom = async () => {
    if (!approval) return
    if (!promoteCode.trim()) {
      setFeedback({
        isOpen: true,
        message: 'Por favor, informe o código do produto.',
        statusOk: false,
      })
      return
    }
    if (!promoteName.trim()) {
      setFeedback({
        isOpen: true,
        message: 'Por favor, informe o nome do produto.',
        statusOk: false,
      })
      return
    }

    setPromotingBusy(true)
    try {
      const created = await ApprovalApi.createShowroom(approval.productId, {
        productCode: promoteCode.trim(),
        name: promoteName.trim(),
      })

      try {
        await ApprovalApi.promote(approval.id)
      } catch (err) {
        console.warn('Erro ao atualizar status do piloto:', err)
      }

      setShowPromoteModal(false)
      onChanged?.()
      setFeedback({
        isOpen: true,
        message: `Mostruário "${created.name}" criado com sucesso!`,
        statusOk: true,
      })
      onClose()
      navigate(`/panel/composition-product/edit/${created.id}?parentId=${approval.productId}`)
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao criar mostruário.',
        statusOk: false,
      })
    } finally {
      setPromotingBusy(false)
    }
  }

  const loadStepsForPipeline = async (pipelineId: number) => {
    setLoadingSteps(true)
    setSelectedTargetStepId(null)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step`,
        { params: { pipelineId } },
      )
      const steps = response.data?.data || []
      setAvailableTargetSteps(steps)
    } catch (err) {
      console.error('Erro ao buscar etapas:', err)
      setAvailableTargetSteps([])
    } finally {
      setLoadingSteps(false)
    }
  }

  const handleOpenSendToPipeline = async () => {
    setShowSendToPipelineModal(true)
    setLoadingPipelines(true)
    try {
      const [moduleRes, customRes] = await Promise.allSettled([
        axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline`,
          { params: { moduleType: 'product_development', subModule: 'fashion' } },
        ),
        axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline`,
        ),
      ])

      const modulePipelines: any[] =
        moduleRes.status === 'fulfilled' ? moduleRes.value.data?.data || [] : []
      const customPipelines: any[] =
        customRes.status === 'fulfilled' ? customRes.value.data?.data || [] : []

      const devPipeline =
        modulePipelines.find(
          (p: any) =>
            p.moduleType === 'product_development' && p.subModule === 'fashion',
        ) ||
        modulePipelines.find(
          (p: any) => p.moduleType === 'product_development',
        ) ||
        modulePipelines[0]

      const availableList: any[] = []
      if (devPipeline) {
        availableList.push(devPipeline)
      }
      customPipelines.forEach((p: any) => {
        if (!p.isModulePipeline && p.id !== devPipeline?.id) {
          availableList.push(p)
        }
      })

      setAvailablePipelines(availableList)

      if (devPipeline) {
        setSelectedTargetPipelineId(devPipeline.id)
        await loadStepsForPipeline(devPipeline.id)
      } else if (availableList.length > 0) {
        setSelectedTargetPipelineId(availableList[0].id)
        await loadStepsForPipeline(availableList[0].id)
      } else {
        setSelectedTargetPipelineId(null)
        setAvailableTargetSteps([])
        setSelectedTargetStepId(null)
      }
    } catch (err) {
      console.error('Erro ao buscar pipelines:', err)
      setFeedback({
        isOpen: true,
        message: 'Erro ao buscar funis disponíveis.',
        statusOk: false,
      })
    } finally {
      setLoadingPipelines(false)
    }
  }

  const handleSelectTargetPipeline = async (pipelineId: number | null) => {
    setSelectedTargetPipelineId(pipelineId)
    if (!pipelineId) {
      setAvailableTargetSteps([])
      setSelectedTargetStepId(null)
      return
    }
    await loadStepsForPipeline(pipelineId)
  }

  const handleConfirmSendToPipeline = async () => {
    if (
      !approval ||
      !selectedTargetPipelineId ||
      !selectedTargetStepId ||
      sendingToPipeline
    ) {
      return
    }
    setSendingToPipeline(true)
    try {
      await ApprovalApi.createCard(approval.id, {
        targetPipelineId: selectedTargetPipelineId,
        targetStepId: selectedTargetStepId,
      })
      setShowSendToPipelineModal(false)
      setFeedback({
        isOpen: true,
        message: 'Card criado no funil com sucesso!',
        statusOk: true,
      })
      window.dispatchEvent(new CustomEvent('pipeline-tickets-refresh'))
      await reload()
      onChanged?.()
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message || 'Erro ao criar card no funil.',
        statusOk: false,
      })
    } finally {
      setSendingToPipeline(false)
    }
  }

  const allItemsApproved =
    !!approval &&
    approval.ChecklistItems.length > 0 &&
    approval.ChecklistItems.every((i) => i.status === 'APPROVED')

  const showPromote =
    !!approval &&
    !locked &&
    approval.type === 'PILOT' &&
    allItemsApproved &&
    isAdmin

  return ReactDOM.createPortal(
    <>
      <Overlay onClick={handleAttemptClose}>
        <ModalShell onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <BackButton type='button' onClick={handleAttemptClose}>
              <ArrowLeft size={16} /> Ficha Técnica
            </BackButton>
            <HeaderTitle>
              <HeaderTitleRow>
                <HeaderProductName>{approval?.Product?.name ?? '...'}</HeaderProductName>
                <HeaderApprovalName>{approval?.name ?? ''}</HeaderApprovalName>
                {approval && <StatusBadge $status={approval.status}>{statusLabel(approval.status)}</StatusBadge>}
                {approval && (
                  <HeaderRef>
                    #{approval.id}
                  </HeaderRef>
                )}
              </HeaderTitleRow>
              {approval && (
                <HeaderSubtitle>
                  Ref. {approval.Product?.productCode ?? '—'} ·{' '}
                  {approval.type === 'PILOT' ? 'Piloto' : 'Mostruário'} ·{' '}
                  {approval.ChecklistItems.filter((i) => i.status === 'APPROVED').length}/
                  {approval.ChecklistItems.length} itens aprovados
                </HeaderSubtitle>
              )}
            </HeaderTitle>
            <HeaderActions>
              {approval?.ticketId ? (
                <OutlineButton type='button' onClick={handleViewCard}>
                  <Eye size={14} /> Ver card
                </OutlineButton>
              ) : (
                <OutlineButton type='button' onClick={handleOpenSendToPipeline}>
                  <PlusSquare size={14} /> Criar card
                </OutlineButton>
              )}
              <OutlineButton type='button' disabled title='Em breve'>
                <Download size={14} /> Exportar PDF
              </OutlineButton>
              {isDirty && (
                <OutlineButton type='button' onClick={handleCancel} disabled={saving}>
                  <X size={14} /> Cancelar
                </OutlineButton>
              )}
              <PrimaryButton
                type='button'
                onClick={handleSave}
                disabled={locked || saving || !isDirty}
              >
                {saving ? <Loader2 size={14} className='spin' /> : <Save size={14} />} Salvar
              </PrimaryButton>
              {showPromote && (
                <PrimaryButton type='button' onClick={handlePromote}>
                  <ShieldCheck size={14} /> Promover a Mostruário
                </PrimaryButton>
              )}
            </HeaderActions>
          </ModalHeader>

          <Body>
            {loading || !approval ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 60,
                  color: '#6b7280',
                }}
              >
                <Loader2 size={24} className='spin' />
                <span style={{ marginLeft: 10, fontSize: 13 }}>Carregando aprovação...</span>
              </div>
            ) : (
            <>
              <Card>
                <SectionTitle>Dados da aprovação</SectionTitle>
                <FieldsGrid>
                  <FieldGroup>
                    <Label>Nome</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={locked || saving}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Tipo</Label>
                    <Input value={approval.type === 'PILOT' ? 'Piloto' : 'Mostruário'} disabled />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Data da prova</Label>
                    <Input
                      type='date'
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      disabled={locked || saving}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Entrega prevista</Label>
                    <Input
                      type='date'
                      value={expectedDeliveryDate}
                      onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                      disabled={locked || saving}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Tamanhos (separados por vírgula)</Label>
                    <Input
                      value={sizesInput}
                      onChange={(e) => setSizesInput(e.target.value)}
                      disabled={locked || saving}
                      placeholder='Ex.: 38, 40, 42'
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Fornecedor</Label>
                    <Input
                      value={approval.SupplierCompany?.companyName ?? '—'}
                      disabled
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Criado por</Label>
                    <Input value={approval.CreatedBy?.name ?? '—'} disabled />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Aprovador</Label>
                    <Input value={approval.Approver?.name ?? '—'} disabled />
                  </FieldGroup>
                </FieldsGrid>
              </Card>

              <ImagesSection
                front={approval.FrontImage}
                side={approval.SideImage}
                back={approval.BackImage}
                onUpload={handleUploadImage}
                onSelectGallery={handleSelectGalleryImage}
                onRemove={handleRemoveImage}
                disabled={locked}
              />

              <ChecklistSection
                approvalId={approval.id}
                items={approval.ChecklistItems}
                isAdmin={isAdmin}
                currentUserId={currentUserId}
                locked={locked}
                onItemsChanged={handleItemsChanged}
              />
            </>
          )}
        </Body>
      </ModalShell>

      {showSendToPipelineModal &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
            }}
            onClick={() => setShowSendToPipelineModal(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: '8px',
                padding: '24px',
                width: '380px',
                maxWidth: '90vw',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>
                Enviar Para Outro Funil
              </h3>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                Selecione o funil destino para criar um subcard:
              </p>
              <select
                value={selectedTargetPipelineId || ''}
                onChange={(e) =>
                  handleSelectTargetPipeline(Number(e.target.value) || null)
                }
                disabled={loadingPipelines}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '13px',
                  marginBottom: '16px',
                }}
              >
                <option value=''>Selecione o funil...</option>
                {availablePipelines.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>

              {selectedTargetPipelineId && (
                <>
                  <p
                    style={{
                      fontSize: '13px',
                      color: '#666',
                      marginBottom: '12px',
                    }}
                  >
                    Etapa do funil: <span style={{ color: '#ef4444' }}>*</span>
                  </p>
                  <select
                    value={selectedTargetStepId || ''}
                    onChange={(e) =>
                      setSelectedTargetStepId(Number(e.target.value) || null)
                    }
                    disabled={loadingSteps}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '13px',
                      marginBottom: '16px',
                    }}
                  >
                    <option value=''>Selecione uma etapa...</option>
                    {availableTargetSteps.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  type='button'
                  onClick={() => setShowSendToPipelineModal(false)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Cancelar
                </button>
                <button
                  type='button'
                  onClick={handleConfirmSendToPipeline}
                  disabled={
                    !selectedTargetPipelineId ||
                    !selectedTargetStepId ||
                    sendingToPipeline
                  }
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#0090D3',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    opacity:
                      !selectedTargetPipelineId ||
                      !selectedTargetStepId ||
                      sendingToPipeline
                        ? 0.5
                        : 1,
                  }}
                >
                  {sendingToPipeline ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      </Overlay>
      {showPromoteModal && (
        <SimpleModalOverlay onClick={() => !promotingBusy && setShowPromoteModal(false)}>
          <SimpleModalBox onClick={(e) => e.stopPropagation()}>
            <SimpleModalHeader>
              <SimpleModalTitle>Promover a Mostruário</SimpleModalTitle>
              <SimpleModalCloseBtn
                type='button'
                onClick={() => !promotingBusy && setShowPromoteModal(false)}
                disabled={promotingBusy}
              >
                <X size={18} />
              </SimpleModalCloseBtn>
            </SimpleModalHeader>
            <SimpleModalBody>
              <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: '1.5' }}>
                Será feita uma cópia do produto atual como mostruário. O registro deste piloto ficará bloqueado para edição.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Label>Código do produto</Label>
                <Input
                  value={promoteCode}
                  onChange={(e) => setPromoteCode(e.target.value)}
                  placeholder='Ex.: teste.1'
                  disabled={promotingBusy}
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Label>Nome do produto</Label>
                <Input
                  value={promoteName}
                  onChange={(e) => setPromoteName(e.target.value)}
                  placeholder='Ex.: Mostruário'
                  disabled={promotingBusy}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Label>Tipo</Label>
                <select
                  value='SHOWROOM'
                  disabled
                  style={{
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    padding: '8px 10px',
                    fontSize: 13,
                    background: '#f3f4f6',
                    color: '#374151',
                    cursor: 'not-allowed',
                  }}
                >
                  <option value='SHOWROOM'>Mostruário</option>
                </select>
              </div>
            </SimpleModalBody>
            <SimpleModalFooter>
              <button
                type='button'
                onClick={() => !promotingBusy && setShowPromoteModal(false)}
                disabled={promotingBusy}
                style={{
                  background: 'none',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  color: '#4b5563',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '8px 14px',
                }}
              >
                Cancelar
              </button>
              <PrimaryButton
                type='button'
                onClick={handleConfirmPromoteToShowroom}
                disabled={promotingBusy || !promoteCode.trim() || !promoteName.trim()}
              >
                {promotingBusy ? (
                  <>
                    <Loader2 size={14} className='spin' /> Criando...
                  </>
                ) : (
                  'Criar e abrir'
                )}
              </PrimaryButton>
            </SimpleModalFooter>
          </SimpleModalBox>
        </SimpleModalOverlay>
      )}
      <ConfirmationModal
        isOpen={showExitConfirm}
        title="Salvar alterações?"
        message="Existem alterações não salvas nos dados da aprovação. Deseja salvar as alterações antes de sair?"
        actions={[
          {
            label: 'Descartar',
            onClick: handleDiscardAndClose,
            variant: 'outline',
          },
          {
            label: saving ? 'Salvando...' : 'Sim, salvar',
            onClick: handleConfirmSaveAndClose,
            variant: 'primary',
          },
        ]}
      />
    </>,
    document.body,
  )
}

export default ApprovationModal
